import type {
	CSSProperties,
	KeyboardEvent as ReactKeyboardEvent,
	PointerEvent as ReactPointerEvent,
} from "react"
import { useState } from "react"
import { flushSync } from "react-dom"

import { cn } from "@productlane/lib/src/cn"

import type { PanelSide } from "./panel-resize"
import type { PanelId } from "@/lib/states/panel-width-state"
import { usePanelWidthState } from "@/lib/states/panel-width-state"
import {
	dragPanelWidth,
	keyboardPanelWidth,
	maxPanelWidth,
	PANEL_SPECS,
	panelWidthValue,
} from "./panel-resize"

export const panelResizeHandleStyles =
	"group/resize absolute inset-y-0 z-40 w-3 cursor-col-resize touch-none select-none outline-none"

export function panelResizeLineStyles(active: boolean) {
	return cn(
		"pointer-events-none absolute inset-y-0 left-[3px] w-0.5",
		active
			? "bg-gray-400 dark:bg-gray-500"
			: // The delay keeps the line from flashing as the pointer crosses the edge; it hides at once.
				"bg-transparent transition-[background-color] duration-0 group-hover/resize:delay-200 group-hover/resize:bg-gray-300 group-focus-visible/resize:bg-gray-400 dark:group-hover/resize:bg-gray-600 dark:group-focus-visible/resize:bg-gray-500",
	)
}

/** Fades the line out at both ends into whichever panel sits behind it. */
const LINE_FADE =
	"linear-gradient(to bottom, transparent, #000 20%, #000 80%, transparent)"
export const panelResizeLineMask: CSSProperties = {
	maskImage: LINE_FADE,
	WebkitMaskImage: LINE_FADE,
}

interface PanelResizeHandleProps {
	panel: PanelId
	label: string
	/** Which side of the handle the panel sits on. */
	panelSide: PanelSide
	getPanel: () => HTMLElement | null
	/** The ancestor carrying the width variable, when it is not the panel. */
	getVarTarget?: () => HTMLElement | null
	/** The flexible content that gives up room as the panel grows. */
	getAbsorber?: () => HTMLElement | null
	className?: string
	style?: CSSProperties
}

const width = (element: HTMLElement) => element.getBoundingClientRect().width

export function PanelResizeHandle({
	panel,
	label,
	panelSide,
	getPanel,
	getVarTarget,
	getAbsorber,
	className,
	style,
}: PanelResizeHandleProps) {
	const spec = PANEL_SPECS[panel]
	const setWidth = usePanelWidthState((s) => s.setWidth)
	const resetWidth = usePanelWidthState((s) => s.resetWidth)
	const [isDragging, setIsDragging] = useState(false)
	const [valueNow, setValueNow] = useState<number>()

	const limitFor = (startWidth: number) => {
		const absorber = getAbsorber?.()
		return maxPanelWidth({
			spec,
			startWidth,
			absorberWidth: absorber ? width(absorber) : undefined,
			viewportWidth: window.innerWidth,
		})
	}

	// The main nav animates width when it hides, which would trail the input.
	const commitInstant = (panelElement: HTMLElement, commit: () => void) => {
		const transition = panelElement.style.transition
		panelElement.style.transition = "none"
		flushSync(commit)
		void panelElement.offsetWidth
		panelElement.style.transition = transition
		setValueNow(Math.round(width(panelElement)))
	}

	const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (event.button !== 0) return
		const panelElement = getPanel()
		const target = getVarTarget?.() ?? panelElement
		if (!panelElement || !target) return
		event.preventDefault()

		const handle = event.currentTarget
		const { pointerId, clientX: startX } = event
		const startWidth = width(panelElement)
		const max = limitFor(startWidth)
		const previousValue = target.style.getPropertyValue(spec.cssVar)
		const previousTransition = panelElement.style.transition
		let next = Math.round(startWidth)
		let moved = false
		let frame = 0
		let finished = false

		const paint = () => {
			frame = 0
			target.style.setProperty(spec.cssVar, `${next}px`)
		}

		const onMove = (moveEvent: PointerEvent) => {
			if (moveEvent.pointerId !== pointerId) return
			next = dragPanelWidth({
				startWidth,
				deltaX: moveEvent.clientX - startX,
				panelSide,
				min: spec.min,
				max,
			})
			moved = true
			if (!frame) frame = requestAnimationFrame(paint)
		}

		const finish = (commit: boolean) => {
			if (finished) return
			finished = true
			cancelAnimationFrame(frame)
			handle.removeEventListener("pointermove", onMove)
			handle.removeEventListener("pointerup", onUp)
			handle.removeEventListener("pointercancel", onCancel)
			handle.removeEventListener("lostpointercapture", onUp)
			window.removeEventListener("keydown", onEscape, true)
			if (handle.hasPointerCapture(pointerId)) {
				handle.releasePointerCapture(pointerId)
			}
			delete document.documentElement.dataset.panelResizing
			panelElement.style.transition = previousTransition
			setIsDragging(false)

			if (commit && moved) {
				target.style.setProperty(spec.cssVar, panelWidthValue(spec, next))
				setWidth(panel, next)
				setValueNow(next)
			} else if (previousValue) {
				target.style.setProperty(spec.cssVar, previousValue)
			} else {
				target.style.removeProperty(spec.cssVar)
			}
		}

		const onUp = () => finish(true)
		const onCancel = () => finish(false)
		const onEscape = (keyEvent: KeyboardEvent) => {
			if (keyEvent.key !== "Escape") return
			keyEvent.preventDefault()
			keyEvent.stopPropagation()
			finish(false)
		}

		handle.setPointerCapture(pointerId)
		handle.addEventListener("pointermove", onMove)
		handle.addEventListener("pointerup", onUp)
		handle.addEventListener("pointercancel", onCancel)
		handle.addEventListener("lostpointercapture", onUp)
		window.addEventListener("keydown", onEscape, true)
		document.documentElement.dataset.panelResizing = ""
		panelElement.style.transition = "none"
		setIsDragging(true)
	}

	const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
		const panelElement = getPanel()
		if (!panelElement) return
		const current = width(panelElement)
		const next = keyboardPanelWidth({
			key: event.key,
			shiftKey: event.shiftKey,
			panelSide,
			current,
			min: spec.min,
			max: limitFor(current),
		})
		if (next === null) return
		event.preventDefault()
		event.stopPropagation()
		commitInstant(panelElement, () => setWidth(panel, next))
	}

	const onDoubleClick = () => {
		const panelElement = getPanel()
		if (!panelElement) return
		commitInstant(panelElement, () => resetWidth(panel))
	}

	const onFocus = () => {
		const panelElement = getPanel()
		if (panelElement) setValueNow(Math.round(width(panelElement)))
	}

	// A focusable separator is the WAI-ARIA window splitter widget, which jsx-a11y reads as static.
	return (
		<div
			role="separator"
			aria-orientation="vertical"
			aria-label={label}
			aria-valuemin={spec.min}
			aria-valuemax={spec.max}
			aria-valuenow={valueNow}
			tabIndex={0}
			data-dragging={isDragging || undefined}
			className={cn(panelResizeHandleStyles, className)}
			style={style}
			onPointerDown={onPointerDown}
			onKeyDown={onKeyDown}
			onDoubleClick={onDoubleClick}
			onFocus={onFocus}
		>
			<div
				className={panelResizeLineStyles(isDragging)}
				style={panelResizeLineMask}
			/>
		</div>
	)
}

/** Sits after a left-hand panel, leaning past its border to clear the scrollbar. */
export function PanelResizeGutter(
	props: Omit<PanelResizeHandleProps, "panelSide" | "style">,
) {
	return (
		<div className="relative w-0 flex-none max-sm:hidden">
			<PanelResizeHandle
				{...props}
				panelSide="left"
				className={cn("-left-1", props.className)}
			/>
		</div>
	)
}
